import { AssetKind, Color, Rect, Vector2, type AssetEntry } from '@kite/engine';
import { type SparkleOptions } from './sparkles';

/*
 * Assets are resolved relative to this module rather than to a public folder, so the game package
 * stays self-contained and the bundler fingerprints each file for us.
 */
const carUrl = new URL('../assets/car.svg', import.meta.url).href;
const coinUrl = new URL('../assets/coin.svg', import.meta.url).href;
const beeUrl = new URL('../assets/bee.svg', import.meta.url).href;
const collectUrl = new URL('../assets/collect.wav', import.meta.url).href;
const hornUrl = new URL('../assets/horn.wav', import.meta.url).href;
const thumbnailUrl = new URL('../assets/thumbnail.svg', import.meta.url).href;

/** Asset key of the car texture. */
export const CAR_KEY = 'car';
/** Asset key of the coin texture. */
export const COIN_KEY = 'coin';
/** Asset key of the bee texture. */
export const BEE_KEY = 'bee';
/** Asset key of the chime played when a coin is taken. */
export const COLLECT_SOUND_KEY = 'collect';
/** Asset key of the blip played when the car is tapped. */
export const HORN_SOUND_KEY = 'horn';

/** URL of the tile the portal shows for this game. */
export const THUMBNAIL_URL = thumbnailUrl;

/** Everything the game loads, in one list the scene hands to the asset manager. */
export const ASSET_MANIFEST: readonly AssetEntry[] = [
  { key: CAR_KEY, url: carUrl, kind: AssetKind.Texture },
  { key: COIN_KEY, url: coinUrl, kind: AssetKind.Texture },
  { key: BEE_KEY, url: beeUrl, kind: AssetKind.Texture },
  { key: COLLECT_SOUND_KEY, url: collectUrl, kind: AssetKind.Audio },
  { key: HORN_SOUND_KEY, url: hornUrl, kind: AssetKind.Audio },
];

/** Collision group the car belongs to. */
export const CAR_GROUP = 1 << 0;
/** Collision group the coins belong to. */
export const COIN_GROUP = 1 << 1;

/**
 * Where coins and the bee are allowed to be.
 *
 * Sized against the *narrowest* supported window rather than the widest. The camera always shows 720
 * units vertically and at 4:3 only 960 horizontally, so anything placed beyond that is off screen for
 * some of the audience; a play area that fits the narrow case fits every case.
 */
export const PLAY_AREA = Rect.fromCenter(0, -30, 860, 500);

/** Backdrop size, wide enough to cover the widest supported aspect ratio with margin to spare. */
export const BACKGROUND_SIZE = new Vector2(1760, 780);

/** Grass the whole scene sits on. */
export const BACKGROUND_COLOR = Color.fromHex('#5fbf6a');
/** The lighter field the play area is drawn on, so the edges of play read as edges. */
export const FIELD_COLOR = Color.fromHex('#7fd18a');
/** The caption, which is aimed at whoever handed over the tablet. */
export const CAPTION_COLOR = Color.fromHex('#1f3d24');

/** Car size in world units; the art is twice as wide as it is tall. */
export const CAR_SIZE = new Vector2(160, 80);
/** Coin diameter in world units, comfortably larger than a toddler's aim is accurate. */
export const COIN_SIZE = new Vector2(90, 90);
/** Bee size in world units. */
export const BEE_SIZE = new Vector2(96, 72);

/** How fast the car chases the pointer, in world units per second. */
export const CAR_SPEED = 620;
/** How fast the bee drifts, in world units per second. */
export const BEE_SPEED = 130;
/** How long the bee keeps heading for one spot before choosing another. */
export const BEE_TURN_SECONDS = 2.5;

/** Starting positions. One coin per entry, spread out so the first frame never looks like a pile. */
export const COIN_POSITIONS: readonly Vector2[] = [
  new Vector2(-330, 150),
  new Vector2(-140, -180),
  new Vector2(60, 90),
  new Vector2(260, -140),
  new Vector2(380, 170),
];

/** How far a fresh coin must land from whatever just took it, so it cannot be collected twice. */
export const COIN_RESPAWN_CLEARANCE = 260;
/** Full pulse cycles a coin breathes through per second. */
export const COIN_PULSE_CYCLES_PER_SECOND = 0.7;
/** Phase offset between neighboring coins, so the field breathes in a wave rather than as one block. */
export const COIN_PULSE_PHASE_STEP_SECONDS = 0.3;

/** Radius of the car's collider, a little tighter than the art so a near miss reads as a miss. */
export const CAR_COLLIDER_RADIUS = 45;
/** Radius of a coin's collider. */
export const COIN_COLLIDER_RADIUS = 45;
/** How far the drawn field extends past the play area, so its edge is not the exact edge of play. */
export const FIELD_CORNER_INSET = 24;

/** The line of text across the top, for the grown-up holding the tablet. */
export const CAPTION_TEXT = 'Move to drive. Tap the car to honk.';
/** Font size of the caption, in world units. */
export const CAPTION_FONT_SIZE = 30;
/** Where the caption sits vertically, above the play area. */
export const CAPTION_Y = 300;

/** Draw order, lowest first, so a change here reads as a change to the layering. */
export const SortOrder = {
  /** The grass. */
  Background: -20,
  /** The lighter field inside it. */
  Field: -10,
  /** Coins waiting to be collected. */
  Coin: 0,
  /** The bee, which drifts over the coins. */
  Bee: 5,
  /** Sparkles from a collected coin. */
  Sparkle: 8,
  /** The car, which is always on top of the play field. */
  Car: 10,
  /** The caption. */
  Caption: 20,
} as const;

/** The burst a collected coin throws out. Declared last, because it reads a draw order from above. */
export const COIN_SPARKLES: SparkleOptions = {
  count: 8,
  radius: 70,
  size: 16,
  lifetimeSeconds: 0.55,
  spinRate: 7,
  color: Color.fromHex('#fff3c4'),
  sortOrder: SortOrder.Sparkle,
};
